import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AttendeeTable from './components/AttendeeTable';
import AttendeeFilters from './components/AttendeeFilters';
import QRScannerModal from './components/QRScannerModal';
import AttendeeDetailsModal from './components/AttendeeDetailsModal';
import MessageModal from './components/MessageModal';
import AttendeeStats from './components/AttendeeStats';


const AttendeeManagement = () => {
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    ticketTier: '',
    checkInStatus: '',
    paymentStatus: '',
    paymentMethod: '',
    purchaseDateFrom: '',
    purchaseDateTo: '',
    amountMin: '',
    amountMax: ''
  });
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showAttendeeDetails, setShowAttendeeDetails] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedAttendeeId, setSelectedAttendeeId] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  // Mock attendee data
  const mockAttendees = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@email.com",
      phone: "+1 (555) 123-4567",
      ticketId: "TKT-SJ2024001",
      ticketTier: "VIP",
      amount: 299,
      purchaseDate: "2024-08-15T10:30:00Z",
      checkInStatus: "checked-in",
      checkInTime: "2024-09-03T08:15:00Z",
      paymentStatus: "paid",
      paymentMethod: "Credit Card",
      transactionId: "TXN-CC-789123",
      company: "Tech Solutions Inc.",
      dietaryRestrictions: "Vegetarian",
      specialNotes: "VIP lounge access requested"
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "michael.chen@email.com",
      phone: "+1 (555) 234-5678",
      ticketId: "TKT-MC2024002",
      ticketTier: "Premium",
      amount: 199,
      purchaseDate: "2024-08-20T14:45:00Z",
      checkInStatus: "not-checked-in",
      paymentStatus: "paid",
      paymentMethod: "PayPal",
      transactionId: "TXN-PP-456789",
      company: "Design Studio",
      discountCode: "EARLY20",
      discountAmount: 40
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.rodriguez@email.com",
      phone: "+1 (555) 345-6789",
      ticketId: "TKT-ER2024003",
      ticketTier: "Standard",
      amount: 99,
      purchaseDate: "2024-08-25T09:20:00Z",
      checkInStatus: "late",
      checkInTime: "2024-09-03T09:45:00Z",
      paymentStatus: "paid",
      paymentMethod: "Apple Pay",
      transactionId: "TXN-AP-123456",
      dietaryRestrictions: "Gluten-free"
    },
    {
      id: 4,
      name: "David Thompson",
      email: "david.thompson@email.com",
      phone: "+1 (555) 456-7890",
      ticketId: "TKT-DT2024004",
      ticketTier: "Premium",
      amount: 199,
      purchaseDate: "2024-08-28T16:10:00Z",
      checkInStatus: "checked-in",
      checkInTime: "2024-09-03T08:30:00Z",
      paymentStatus: "paid",
      paymentMethod: "Credit Card",
      transactionId: "TXN-CC-987654",
      company: "Marketing Agency"
    },
    {
      id: 5,
      name: "Lisa Wang",
      email: "lisa.wang@email.com",
      phone: "+1 (555) 567-8901",
      ticketId: "TKT-LW2024005",
      ticketTier: "VIP",
      amount: 299,
      purchaseDate: "2024-08-30T11:55:00Z",
      checkInStatus: "not-checked-in",
      paymentStatus: "pending",
      paymentMethod: "Bank Transfer",
      transactionId: "TXN-BT-654321",
      specialNotes: "Accessibility requirements - wheelchair access"
    },
    {
      id: 6,
      name: "James Wilson",
      email: "james.wilson@email.com",
      phone: "+1 (555) 678-9012",
      ticketId: "TKT-JW2024006",
      ticketTier: "Standard",
      amount: 99,
      purchaseDate: "2024-09-01T13:25:00Z",
      checkInStatus: "not-checked-in",
      paymentStatus: "paid",
      paymentMethod: "Google Pay",
      transactionId: "TXN-GP-321987",
      company: "Startup Hub"
    },
    {
      id: 7,
      name: "Maria Garcia",
      email: "maria.garcia@email.com",
      phone: "+1 (555) 789-0123",
      ticketId: "TKT-MG2024007",
      ticketTier: "Premium",
      amount: 159,
      purchaseDate: "2024-09-02T08:40:00Z",
      checkInStatus: "checked-in",
      checkInTime: "2024-09-03T08:05:00Z",
      paymentStatus: "paid",
      paymentMethod: "Credit Card",
      transactionId: "TXN-CC-147258",
      discountCode: "STUDENT15",
      discountAmount: 40,
      dietaryRestrictions: "Vegan"
    },
    {
      id: 8,
      name: "Robert Kim",
      email: "robert.kim@email.com",
      phone: "+1 (555) 890-1234",
      ticketId: "TKT-RK2024008",
      ticketTier: "VIP",
      amount: 299,
      purchaseDate: "2024-08-18T15:30:00Z",
      checkInStatus: "not-checked-in",
      paymentStatus: "refunded",
      paymentMethod: "Credit Card",
      transactionId: "TXN-CC-852963",
      specialNotes: "Refund processed due to schedule conflict"
    }
  ];

  // Filter attendees based on current filters
  const filteredAttendees = useMemo(() => {
    return mockAttendees?.filter(attendee => {
      // Search filter
      if (filters?.search) {
        const searchTerm = filters?.search?.toLowerCase();
        const matchesSearch = 
          attendee?.name?.toLowerCase()?.includes(searchTerm) ||
          attendee?.email?.toLowerCase()?.includes(searchTerm) ||
          attendee?.ticketId?.toLowerCase()?.includes(searchTerm);
        if (!matchesSearch) return false;
      }

      // Ticket tier filter
      if (filters?.ticketTier && attendee?.ticketTier !== filters?.ticketTier) {
        return false;
      }

      // Check-in status filter
      if (filters?.checkInStatus && attendee?.checkInStatus !== filters?.checkInStatus) {
        return false;
      }

      // Payment status filter
      if (filters?.paymentStatus && attendee?.paymentStatus !== filters?.paymentStatus) {
        return false;
      }

      // Payment method filter
      if (filters?.paymentMethod && attendee?.paymentMethod !== filters?.paymentMethod) {
        return false;
      }

      // Date range filters
      if (filters?.purchaseDateFrom) {
        const purchaseDate = new Date(attendee.purchaseDate);
        const fromDate = new Date(filters.purchaseDateFrom);
        if (purchaseDate < fromDate) return false;
      }

      if (filters?.purchaseDateTo) {
        const purchaseDate = new Date(attendee.purchaseDate);
        const toDate = new Date(filters.purchaseDateTo);
        toDate?.setHours(23, 59, 59, 999); // End of day
        if (purchaseDate > toDate) return false;
      }

      // Amount range filters
      if (filters?.amountMin && attendee?.amount < parseFloat(filters?.amountMin)) {
        return false;
      }

      if (filters?.amountMax && attendee?.amount > parseFloat(filters?.amountMax)) {
        return false;
      }

      return true;
    });
  }, [filters]);

  // Get selected attendee details
  const selectedAttendee = selectedAttendeeId 
    ? mockAttendees?.find(a => a?.id === selectedAttendeeId)
    : null;

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type });
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Handle attendee selection
  const handleSelectAttendee = (attendeeId, isSelected) => {
    if (isSelected) {
      setSelectedAttendees(prev => [...prev, attendeeId]);
    } else {
      setSelectedAttendees(prev => prev?.filter(id => id !== attendeeId));
    }
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedAttendees(filteredAttendees?.map(a => a?.id));
    } else {
      setSelectedAttendees([]);
    }
  };

  // Handle check-in
  const handleCheckIn = (attendeeId) => {
    showToast(`Successfully checked in attendee`, 'success');
    // In real app, update attendee status
  };

  // Handle bulk check-in
  const handleBulkCheckIn = (attendeeIds) => {
    showToast(`Successfully checked in ${attendeeIds?.length} attendees`, 'success');
    setSelectedAttendees([]);
  };

  // Handle QR scan success
  const handleQRScanSuccess = (ticketId) => {
    const attendee = mockAttendees?.find(a => a?.ticketId === ticketId);
    if (attendee) {
      handleCheckIn(attendee?.id);
      setShowQRScanner(false);
      showToast(`Checked in ${attendee?.name} successfully`, 'success');
    } else {
      showToast('Ticket not found', 'error');
    }
  };

  // Handle manual ticket entry
  const handleManualEntry = (ticketId) => {
    const attendee = mockAttendees?.find(a => a?.ticketId?.toLowerCase() === ticketId?.toLowerCase());
    if (attendee) {
      handleCheckIn(attendee?.id);
      setShowQRScanner(false);
      showToast(`Checked in ${attendee?.name} successfully`, 'success');
    } else {
      showToast('Ticket ID not found', 'error');
    }
  };

  // Handle export
  const handleExport = () => {
    showToast('Attendee list exported successfully', 'success');
  };

  // Handle send message - enhanced with SMS support
  const handleSendMessage = (messageData) => {
    let successMessage = '';
    
    if (messageData?.emailCount && messageData?.smsCount) {
      successMessage = `Message sent via email to ${messageData?.emailCount} attendees and SMS to ${messageData?.smsCount} attendees`;
    } else if (messageData?.emailCount) {
      successMessage = `Email sent to ${messageData?.emailCount} attendees`;
    } else if (messageData?.smsCount) {
      successMessage = `SMS sent to ${messageData?.smsCount} attendees`;
    } else {
      successMessage = `Message sent to ${messageData?.recipients} attendees`;
    }

    if (messageData?.errors?.length > 0) {
      successMessage += `. Note: ${messageData?.errors?.join(', ')}`;
    }

    showToast(successMessage, messageData?.errors?.length > 0 ? 'warning' : 'success');
  };

  // Handle view details
  const handleViewDetails = (attendeeId) => {
    setSelectedAttendeeId(attendeeId);
    setShowAttendeeDetails(true);
  };

  // Handle process refund
  const handleProcessRefund = (attendeeId) => {
    const attendee = mockAttendees?.find(a => a?.id === attendeeId);
    if (attendee) {
      showToast(`Refund initiated for ${attendee?.name}`, 'success');
    }
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      search: '',
      ticketTier: '',
      checkInStatus: '',
      paymentStatus: '',
      paymentMethod: '',
      purchaseDateFrom: '',
      purchaseDateTo: '',
      amountMin: '',
      amountMax: ''
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
                <Link to="/host-dashboard" className="hover:text-foreground transition-colors">
                  Host Dashboard
                </Link>
                <Icon name="ChevronRight" size={14} />
                <span className="text-foreground">Attendee Management</span>
              </nav>
              <h1 className="text-2xl font-bold text-foreground">Attendee Management</h1>
              <p className="text-muted-foreground mt-1">
                Track and manage event participants with check-in capabilities
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowQRScanner(true)}
                iconName="QrCode"
                iconPosition="left"
              >
                QR Scanner
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowMessageModal(true)}
                iconName="Mail"
                iconPosition="left"
              >
                Send Message
              </Button>
              <Button
                variant="primary"
                onClick={handleExport}
                iconName="Download"
                iconPosition="left"
              >
                Export Data
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats Overview */}
        <AttendeeStats 
          attendees={mockAttendees}
          filteredAttendees={filteredAttendees}
        />

        {/* Filters */}
        <AttendeeFilters
          filters={filters}
          onFiltersChange={setFilters}
          onClearFilters={handleClearFilters}
          totalCount={mockAttendees?.length}
          filteredCount={filteredAttendees?.length}
        />

        {/* Attendee Table */}
        <AttendeeTable
          attendees={filteredAttendees}
          onCheckIn={handleCheckIn}
          onBulkCheckIn={handleBulkCheckIn}
          onExport={handleExport}
          onSendMessage={() => setShowMessageModal(true)}
          onViewDetails={handleViewDetails}
          onProcessRefund={handleProcessRefund}
          selectedAttendees={selectedAttendees}
          onSelectAttendee={handleSelectAttendee}
          onSelectAll={handleSelectAll}
        />
      </div>
      {/* Modals */}
      <QRScannerModal
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onScanSuccess={handleQRScanSuccess}
        onManualEntry={handleManualEntry}
      />
      <AttendeeDetailsModal
        isOpen={showAttendeeDetails}
        onClose={() => setShowAttendeeDetails(false)}
        attendee={selectedAttendee}
        onCheckIn={handleCheckIn}
        onProcessRefund={handleProcessRefund}
      />
      <MessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        onSendMessage={handleSendMessage}
        selectedAttendees={selectedAttendees}
        allAttendees={mockAttendees}
      />
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <div className={`flex items-center space-x-3 px-4 py-3 rounded-lg shadow-lg ${
            toastMessage?.type === 'success' ?'bg-success text-success-foreground' :'bg-error text-error-foreground'
          }`}>
            <Icon 
              name={toastMessage?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
              size={16} 
            />
            <span className="text-sm font-medium">{toastMessage?.message}</span>
            <button
              onClick={() => setToastMessage(null)}
              className="hover:opacity-80"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendeeManagement;